import React, { useState, useEffect, useRef } from 'react';
import DeviceSelector from './DeviceSelector';
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import type { Device as DeviceType } from '../../../shared/types/models/device';
import type { Grantor } from '../../../shared/types/models/grantor';

interface CameraModalProps {
  open: boolean;
  onClose: () => void;
  entityLookup: string | null;
  entityName?: string;
  biometricType?: string;
}

const CameraModal: React.FC<CameraModalProps> = ({ open, onClose, entityLookup, biometricType: biometricTypeProp }) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [loading, setLoading] = useState(false);
  const { apiBaseUrl, entities, setEntities } = useUserContext();
  const { getToken } = useKeycloak();
  const prevOpen = useRef<boolean>(open);
  const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);

  const handleTakePhoto = async () => {
    if (!selectedDevice || !entityLookup) {
      console.warn('[CameraModal] Missing selectedDevice or entityLookup');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      // Use the biometricType prop passed from BiometricsCard (which already resolved it via grantorId matching).
      // Fall back to entity lookup for backward compatibility.
      const currentEntity = entities.find((e: any) => String(e.lookup) === String(entityLookup));
      const biometricType = biometricTypeProp || currentEntity?.biometricType;
      const isCompare = biometricType === 'compare';
      console.log('[CameraModal] entityLookup:', entityLookup);
      console.log('[CameraModal] biometricTypeProp:', biometricTypeProp);
      console.log('[CameraModal] resolvedBiometricType:', biometricType);
      let res, data;
      if (isCompare) {
        res = await fetch(`${apiBaseUrl}/api/frozendeed/entity/${entityLookup}/compare`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ deviceId: selectedDevice.uuid }),
        });
        data = await res.json();
        if (data.success) {
          setSuccess(true);
          setError(null);
          if (entityLookup) {
            // Refetch the updated entity from the backend
            try {
              const entityRes = await fetch(`${apiBaseUrl}/api/frozendeed/entity/${entityLookup}`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (entityRes.ok) {
                const entityData = await entityRes.json();
                if (entityData?.entity || entityData?.data) {
                  const updatedEntity = entityData.entity || entityData.data;
                  setEntities(entities.map((e: Grantor) => String((e as any).lookup) === String(entityLookup) ? updatedEntity : e));
                }
              }
            } catch (fetchErr) {
              console.error('Failed to refetch entity after photo capture:', fetchErr);
            }
          }
        } else {
          setError(data.error?.message || data.errorMessage || 'Unknown error');
          setSuccess(false);
        }
      } else {
        // Default to enroll if not compare
        res = await fetch(`${apiBaseUrl}/api/frozendeed/entity/${entityLookup}/enroll`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ deviceId: selectedDevice.uuid, comments: '' }),
        });
        data = await res.json();
        const contentError = Array.isArray((data as any).content) && (data as any).content[0]?.errorMessage;
        if (contentError) {
          setError(contentError);
          setSuccess(false);
        } else if (data.success) {
          setSuccess(true);
          setError(null);
          if (entityLookup) {
            // Refetch the updated entity from the backend
            try {
              const entityRes = await fetch(`${apiBaseUrl}/api/frozendeed/entity/${entityLookup}`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (entityRes.ok) {
                const entityData = await entityRes.json();
                if (entityData?.entity || entityData?.data) {
                  const updatedEntity = entityData.entity || entityData.data;
                  setEntities(entities.map((e: Grantor) => String((e as any).lookup) === String(entityLookup) ? updatedEntity : e));
                }
              }
            } catch (fetchErr) {
              console.error('Failed to refetch entity after photo capture:', fetchErr);
            }
          }
        } else {
          setError(data.errorMessage || 'Unknown error');
          setSuccess(false);
        }
      }
    } catch (err) {
      console.error('[CameraModal] Failed to enroll/compare:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (open && !prevOpen.current) {
      setSuccess(false);
      setError(null);
      setLoading(false);
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        setAutoCloseTimer(null);
      }
    }
    prevOpen.current = open;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      setAutoCloseTimer(timer);
      return () => clearTimeout(timer);
    } else if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }
  }, [success, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center relative min-w-[350px] max-w-[90vw]"
        onClick={e => e.stopPropagation()}
        style={{ minWidth: 350, maxWidth: 400 }}
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-[#485D7D] hover:text-[#141435] text-2xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {/* Camera Icon */}
        <div className="mb-6 mt-2">
          {/* SVG camera icon, styled to match Figma */}
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <rect x="8" y="16" width="44" height="32" rx="8" fill="#F0F6FF"/>
            <rect x="8" y="16" width="44" height="32" rx="8" stroke="#0B4BB6" strokeWidth="2"/>
            <circle cx="30" cy="32" r="10" fill="#0B4BB6"/>
            <circle cx="30" cy="32" r="6" fill="#fff"/>
            <rect x="24" y="10" width="12" height="8" rx="4" fill="#0B4BB6"/>
          </svg>
        </div>
        {/* Title */}
        <div className="text-[24px] font-plus-jakarta font-semibold text-[#141435] mb-2 text-center">{biometricTypeProp === 'compare' ? 'Verify identity' : 'Camera ready!'}</div>
        {/* Subtitle */}
        <div className="text-[16px] font-plus-jakarta text-[#485D7D] mb-6 text-center max-w-xs">
          Make sure the customer is standing behind the line before taking a photo.
        </div>
        {/* Device Selector */}
        <div className="w-full mb-4">
          <DeviceSelector value={selectedDevice} onChange={setSelectedDevice} label="" dropdownAnchor="top" />
        </div>
        {/* Take Photo Button or Success */}
        {success ? (
          <>
            <div className="flex flex-col items-center gap-2 mb-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#22c55e"/><path d="M6 10.5l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-[#3C7F3C] font-plus-jakarta font-bold text-[16px]">{biometricTypeProp === 'compare' ? 'Identity verified!' : 'Photo captured!'}</span>
            </div>
            <button
              className="mt-4 w-full bg-[#0B4BB6] text-white font-plus-jakarta font-bold py-2.5 rounded-full hover:bg-[#23235a] transition-colors"
              onClick={() => {
                if (autoCloseTimer) {
                  clearTimeout(autoCloseTimer);
                  setAutoCloseTimer(null);
                }
                onClose();
              }}
            >
              OK
            </button>
          </>
        ) : (
          <>
            {error && (
              <div
                className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 font-plus-jakarta font-semibold mb-3 px-4 py-2 rounded-lg shadow-sm text-center justify-center"
                role="alert"
                style={{ minHeight: 44 }}
              >
                {/* Error icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 text-red-500 flex-shrink-0"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button
              className={`flex items-center gap-2 bg-[#0B4BB6] border-2 border-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full transition-colors mb-2 ${!selectedDevice || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
              style={{ borderRadius: 60 }}
              onClick={handleTakePhoto}
              disabled={!selectedDevice || loading}
            >
              {loading && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              )}
              {loading ? (biometricTypeProp === 'compare' ? 'Verifying...' : 'Taking Photo...') : (biometricTypeProp === 'compare' ? 'Verify Identity' : 'Take Photo')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraModal; 